import { useState } from "react";
import { ArrowLeft, ArrowRight, Star, EyeOff, FileText, Video, GripVertical, Plus } from "lucide-react";
import { mediaRawUrl } from "@/lib/api";
import { adSequence, aid, isPdfItem, saveSequence, withInserted, AD_KINDS } from "@/lib/adMedia";
import { SubHead } from "@/components/AdPhotos";

const LABEL = { image: "Photo", video: "Video", paper: "Papers" };

export function Thumb({ horseId, m, className = "" }) {
  const src = mediaRawUrl(horseId, aid(m), m.rotation);
  if (m.kind === "video") return <div className={`flex items-center justify-center bg-stone-800 text-white ${className}`}><Video className="h-6 w-6" /></div>;
  if (m.kind === "paper" && isPdfItem(m)) return <div className={`flex flex-col items-center justify-center bg-stone-50 text-red-700 ${className}`}><FileText className="h-7 w-7" /><span className="text-[10px] text-stone-600 font-semibold">PDF</span></div>;
  return <img src={src} alt={m.name} loading="lazy" draggable={false} className={`object-cover ${className}`} />;
}

function OrderTile({ m, i, n, horseId, locked, move, toMain, drop, exclude }) {
  const [over, setOver] = useState(false);
  const t = `ad-order-${aid(m)}`;
  const btn = "h-11 w-11 shrink-0 sm:h-9 sm:w-9 rounded-full flex items-center justify-center text-stone-700 hover:bg-stone-100 disabled:opacity-30";
  return (
    <div data-testid={t} draggable={!locked}
      onDragStart={(e) => e.dataTransfer.setData("text/plain", String(i))}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); drop(Number(e.dataTransfer.getData("text/plain")), i); }}
      className={`rounded-xl border bg-white overflow-hidden transition-colors duration-150 ${over ? "border-amber-700 ring-2 ring-amber-200" : "border-stone-200"} ${locked ? "" : "sm:cursor-grab"}`}>
      <div className="relative aspect-[4/3] bg-stone-100">
        <Thumb horseId={horseId} m={m} className="w-full h-full" />
        <span data-testid={`${t}-pos`} className="absolute top-1.5 left-1.5 rounded-full bg-stone-900/80 text-white text-[11px] font-bold px-2 py-0.5">#{i + 1}{i === 0 && m.kind === "image" && " Main"}</span>
        <span className="absolute bottom-1.5 left-1.5 rounded-full bg-white/90 text-stone-700 text-[10px] font-semibold px-2 py-0.5">{LABEL[m.kind]}</span>
        {!locked && <GripVertical className="hidden sm:block absolute top-1.5 right-1.5 h-4 w-4 text-white drop-shadow" />}
      </div>
      {!locked && (
        <div className="flex items-center justify-between p-1">
          <button type="button" data-testid={`${t}-left`} disabled={i === 0} onClick={() => move(i, -1)} className={btn} aria-label="Move earlier"><ArrowLeft className="h-4 w-4" /></button>
          <button type="button" data-testid={`${t}-right`} disabled={i === n - 1} onClick={() => move(i, 1)} className={btn} aria-label="Move later"><ArrowRight className="h-4 w-4" /></button>
          {m.kind === "image" && <button type="button" data-testid={`${t}-main`} disabled={i === 0} onClick={() => toMain(i)} className={btn} aria-label="Make main photo"><Star className="h-4 w-4" /></button>}
          {m.kind !== "paper" && <button type="button" data-testid={`${t}-exclude`} onClick={() => exclude(m)} className={btn} aria-label="Leave out of ad"><EyeOff className="h-4 w-4" /></button>}
        </div>
      )}
    </div>
  );
}

export function AdOrder({ horseId, media, locked, onChange, onExclude }) {
  const seq = adSequence(media);
  const save = (next) => saveSequence(horseId, media, next, onChange);
  const drop = (from, to) => {
    if (Number.isNaN(from) || from === to) return;
    const next = [...seq]; const [x] = next.splice(from, 1); next.splice(to, 0, x); save(next);
  };
  const move = (i, d) => drop(i, i + d);
  const toMain = (i) => drop(i, 0);
  return (
    <SubHead letter="D" title="Advertisement Order" testId="ad-order-section">
      <p className="text-sm text-stone-600">Everything that will appear in your ad, in order. <strong>#1</strong> is the first thing buyers see. Use the arrows{!locked && <span className="hidden sm:inline"> or drag and drop</span>} to reorder; the star makes a photo the main photo.</p>
      {seq.length ? (
        <div data-testid="ad-order-list" className="grid grid-cols-1 min-[440px]:grid-cols-2 sm:grid-cols-3 gap-3">
          {seq.map((m, i) => <OrderTile key={aid(m)} m={m} i={i} n={seq.length} horseId={horseId} locked={locked} move={move} toMain={toMain} drop={drop} exclude={onExclude} />)}
        </div>
      ) : <p data-testid="ad-order-empty" className="text-sm text-stone-500 bg-stone-50 border border-dashed border-stone-300 rounded-xl p-4">Nothing is included in the ad yet.</p>}
      <NotInAd horseId={horseId} items={(media || []).filter((m) => AD_KINDS.includes(m.kind) && m.kind !== "paper" && !m.ad_order)} locked={locked} add={(m) => save(withInserted(seq, m))} />
    </SubHead>
  );
}

function NotInAd({ horseId, items, locked, add }) {
  if (!items.length) return null;
  return (
    <div data-testid="ad-order-excluded" className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Not in your ad (still saved)</p>
      <ul className="space-y-2">
        {items.map((m) => (
          <li key={aid(m)} data-testid={`ad-order-excluded-${aid(m)}`} className="flex items-center gap-3">
            <Thumb horseId={horseId} m={m} className="h-12 w-12 rounded-md shrink-0" />
            <span className="min-w-0 flex-1"><span className="block text-sm text-stone-800 truncate">{m.name || LABEL[m.kind]}</span><span className="text-[11px] text-stone-500">{LABEL[m.kind]}</span></span>
            {!locked && <button type="button" data-testid={`ad-order-add-${aid(m)}`} onClick={() => add(m)}
              className="shrink-0 inline-flex items-center gap-1 h-11 sm:h-9 px-3 rounded-full border border-amber-800 text-amber-800 text-sm font-semibold hover:bg-amber-50 transition-colors duration-150"><Plus className="h-4 w-4" />Add to Ad</button>}
          </li>
        ))}
      </ul>
    </div>
  );
}
